import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, Filter, Plus, Receipt, ShoppingCart } from 'lucide-react';
import { api } from '../services/api.js';
import type { Bestellung, Lieferschein } from '../types';
import { NewOrderModal } from '../components/orders/NewOrderModal.js';
import { DocumentDetailSheet } from '../components/documents/DocumentDetailSheet';
import { OwnDeliveryPanel } from '../components/orders/OwnDeliveryPanel';
import { CreatePickListSheet } from '../components/orders/CreatePickListSheet';
import type { PackingCard } from '../components/orders/OwnDeliveryPanel';
import type { OpenOwnOrder } from '../utils/pickListGrouping';
import type { AttachedDocumentRef } from '../utils/documentToast';
import { useLongPressSelect } from '../hooks/useLongPressSelect';
import { SelectionBar } from '../components/ui/SelectionBar';
import { DeleteDocumentDialog } from '../components/ui/DeleteDocumentDialog';
import { SelectableDocumentList } from '../components/documents/SelectableDocumentList';
import { extractApiError } from '../utils/pdfDownload';

type OrdersTab = 'own' | 'distributors';
type SheetKind = 'lieferschein' | 'bestellung';

interface SheetDoc {
  kind: SheetKind;
  id: string;
  number: string;
  status: string;
  pos_name?: string;
  pos_city?: string;
  manufacturer_name?: string;
  distributor_id?: string;
  distributor_name?: string;
  total_net: number;
  total_pfand: number;
  total_gross: number;
  items?: Lieferschein['items'];
}

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: OrdersTab =
    searchParams.get('tab') === 'distributors' ? 'distributors' : 'own';
  const [ownOrders, setOwnOrders] = useState<OpenOwnOrder[]>([]);
  const [packing, setPacking] = useState<PackingCard[]>([]);
  const [poRows, setPoRows] = useState<Bestellung[]>([]);
  const [poFilter, setPoFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SheetDoc | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const initialPosId = searchParams.get('posId') || undefined;

  useEffect(() => {
    if (searchParams.get('new') === '1') setShowCreate(true);
  }, [searchParams]);

  const closeCreate = () => {
    setShowCreate(false);
    if (searchParams.get('new') || searchParams.get('posId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      next.delete('posId');
      setSearchParams(next, { replace: true });
    }
  };
  const [pickOpen, setPickOpen] = useState(false);
  const [pickListId, setPickListId] = useState<string | null>(null);
  const poSelect = useLongPressSelect();
  const [poDeleteOpen, setPoDeleteOpen] = useState(false);

  const setTab = (next: OrdersTab) => {
    if (next === 'distributors') setSearchParams({ tab: 'distributors' });
    else setSearchParams({});
  };

  const fetchLists = async () => {
    try {
      const [ownRes, packRes, poRes] = await Promise.all([
        api.get('/orders/open-own'),
        api.get('/pick-lists'),
        api.get('/bestellungen'),
      ]);
      setOwnOrders(ownRes.data || []);
      setPacking(packRes.data || []);
      setPoRows(poRes.data || []);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchLists();
  }, []);

  const openDetail = async (
    kind: SheetKind,
    id: string,
    silent = false
  ): Promise<boolean> => {
    try {
      const base = kind === 'lieferschein' ? '/lieferscheine' : '/bestellungen';
      const res = await api.get(`${base}/${id}`);
      const row = res.data;
      setSelected({
        kind,
        id: row.id,
        number: kind === 'lieferschein' ? row.lieferschein_number : row.bestellung_number,
        status: row.status,
        pos_name: row.pos_name,
        pos_city: row.pos_city,
        manufacturer_name: row.manufacturer_name,
        distributor_id: row.distributor_id,
        distributor_name: row.distributor_name,
        total_net: row.total_net,
        total_pfand: row.total_pfand,
        total_gross: row.total_gross,
        items: row.items,
      });
      return true;
    } catch {
      if (!silent) toast.error('Failed to load details');
      setSelected(null);
      return false;
    }
  };

  const refreshSelected = async (docs?: AttachedDocumentRef[]) => {
    const moved = docs?.[0];
    if (moved) {
      if (moved.kind === 'bestellung') setTab('distributors');
      await fetchLists();
      await openDetail(moved.kind, moved.id, true);
      return;
    }
    await fetchLists();
    if (!selected) return;
    await openDetail(selected.kind, selected.id, true);
  };

  const onPickConfirmed = async (
    documents: AttachedDocumentRef[],
    batchId?: string | null
  ) => {
    await fetchLists();
    if (batchId) {
      navigate('/delivery-batches');
      return;
    }
    const first = documents[0];
    if (first) await openDetail(first.kind, first.id, true);
  };

  const visiblePo = poRows.filter((row) => {
    if (poFilter === 'open') return row.status === 'draft' || row.status === 'finalized';
    if (poFilter === '') return true;
    return row.status === poFilter;
  });

  const deleteSelectedPos = async (deleteOrders: boolean) => {
    const ids = [...poSelect.selected];
    try {
      for (const id of ids) {
        await api.delete(`/bestellungen/${id}`, {
          params: { delete_orders: deleteOrders },
        });
      }
      toast.success('Deleted');
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      poSelect.exit();
      await fetchLists();
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-3">
        <div>
          <h1 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <ShoppingCart size={28} className="text-brand-500" />
            Orders
          </h1>
          <p className="text-gray-500 font-medium text-sm">
            Pack Own Delivery orders, then send purchase orders to distributors
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-white rounded-xl font-bold text-xs hover:bg-brand-600"
        >
          <Plus size={14} />
          New order
        </button>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setTab('own')}
          className={`flex-1 min-h-[48px] rounded-xl text-sm font-bold ${
            tab === 'own' ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600'
          }`}
        >
          Own delivery ({ownOrders.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('distributors')}
          className={`flex-1 min-h-[48px] rounded-xl text-sm font-bold ${
            tab === 'distributors' ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600'
          }`}
        >
          To distributors ({visiblePo.length})
        </button>
      </div>

      {tab === 'distributors' && (
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <div className="relative">
            <select
              value={poFilter}
              onChange={(e) => setPoFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2.5 rounded-xl font-bold text-xs bg-gray-100 text-gray-600 border-0"
            >
              <option value="open">Needs action</option>
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="finalized">Finalized</option>
              <option value="sent">Sent</option>
            </select>
            <ChevronDown
              size={14}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
        </div>
      )}

      <NewOrderModal
        open={showCreate}
        onClose={closeCreate}
        onCreated={() => void fetchLists()}
        initialPosId={initialPosId}
      />
      {pickOpen ? (
        <CreatePickListSheet
          open={pickOpen}
          listId={pickListId}
          onClose={() => {
            setPickOpen(false);
            setPickListId(null);
            void fetchLists();
          }}
          onConfirmed={(docs, batchId) => void onPickConfirmed(docs, batchId)}
        />
      ) : null}

      {loading ? (
        <div className="text-center py-20 text-gray-400 font-bold">Loading...</div>
      ) : tab === 'own' ? (
        <OwnDeliveryPanel
          packing={packing}
          orders={ownOrders}
          onCreate={() => {
            setPickListId(null);
            setPickOpen(true);
          }}
          onContinue={(id) => {
            setPickListId(id);
            setPickOpen(true);
          }}
          onMoved={(docs) => void refreshSelected(docs)}
          onOrdersChanged={() => void fetchLists()}
        />
      ) : visiblePo.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-gray-300">
          <Receipt size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-bold text-gray-600">No purchase orders in this filter</p>
        </div>
      ) : (
        <div>
          <SelectableDocumentList
            rows={visiblePo.map((bs) => ({
              id: bs.id,
              title: bs.bestellung_number,
              subtitle: [
                bs.pos_name || 'No PoS',
                bs.manufacturer_name,
                bs.distributor_name,
              ]
                .filter(Boolean)
                .join(' · '),
              status: bs.status,
            }))}
            selecting={poSelect.mode}
            selected={poSelect.selected}
            bindRow={poSelect.bindRow}
            onOpen={(id) => void openDetail('bestellung', id)}
          />
          {poSelect.mode ? (
            <SelectionBar
              count={poSelect.selected.size}
              onDone={poSelect.exit}
              onDelete={() => setPoDeleteOpen(true)}
            />
          ) : null}
          <DeleteDocumentDialog
            open={poDeleteOpen}
            onOpenChange={setPoDeleteOpen}
            title="Delete purchase orders?"
            message={visiblePo
              .filter((row) => poSelect.selected.has(row.id))
              .map((row) => row.bestellung_number)
              .join(', ')}
            onDeleteDocument={() => deleteSelectedPos(false)}
            onDeleteDocumentAndOrders={() => deleteSelectedPos(true)}
          />
        </div>
      )}

      <DocumentDetailSheet
        open={Boolean(selected)}
        kind={selected?.kind || 'lieferschein'}
        doc={
          selected
            ? {
                id: selected.id,
                number: selected.number,
                status: selected.status,
                pos_name: selected.pos_name,
                pos_city: selected.pos_city,
                manufacturer_name: selected.manufacturer_name,
                distributor_id: selected.distributor_id,
                distributor_name: selected.distributor_name,
                total_net: selected.total_net,
                total_pfand: selected.total_pfand,
                total_gross: selected.total_gross,
                items: selected.items,
              }
            : null
        }
        onClose={() => setSelected(null)}
        onRefresh={(docs) => void refreshSelected(docs)}
      />
    </div>
  );
};

export default Orders;
